import axios from 'axios';

// Short-lived access token + rotating refresh token for the web app.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'token';
const REFRESH_KEY = 'refresh_token';
const nativeFetch = window.fetch.bind(window);
let inflight = null;

export function storeSession(data) {
  if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
  if (data?.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

function tokenExpiryMs(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(part)).exp * 1000;
  } catch {
    return 0;
  }
}

export function refreshSession() {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return Promise.resolve(null);
  if (!inflight) {
    inflight = nativeFetch(`${API}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Mode': 'refresh' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          storeSession(data);
          axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
          return data.token;
        }
        if (res.status === 401) {
          clearSession();
          window.dispatchEvent(new Event('auth:expired'));
        }
        return null;
      })
      .catch(() => null)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function endSession() {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (refreshToken) {
    nativeFetch(`${API}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }).catch(() => {});
  }
  clearSession();
}

function installAxios() {
  axios.interceptors.request.use((config) => {
    config.headers['X-Auth-Mode'] = 'refresh';
    return config;
  });
  axios.interceptors.response.use(
    (res) => {
      if (res.data?.token && res.data?.refresh_token) storeSession(res.data);
      return res;
    },
    async (error) => {
      const config = error.config;
      const canRetry = error.response?.status === 401 && config && !config._authRetried
        && !String(config.url || '').includes('/auth/refresh') && localStorage.getItem(REFRESH_KEY);
      if (canRetry) {
        config._authRetried = true;
        const token = await refreshSession();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          return axios(config);
        }
      }
      return Promise.reject(error);
    }
  );
}

function installFetch() {
  window.fetch = async (input, init = {}) => {
    if (typeof input !== 'string' || !input.startsWith(API)) return nativeFetch(input, init);
    const headers = new Headers(init.headers || {});
    headers.set('X-Auth-Mode', 'refresh');
    let res = await nativeFetch(input, { ...init, headers });
    if (res.ok && input.includes('/auth/')) {
      res.clone().json().then((d) => { if (d?.token && d?.refresh_token) storeSession(d); }).catch(() => {});
    }
    if (res.status === 401 && headers.get('Authorization') && !input.includes('/auth/refresh') && localStorage.getItem(REFRESH_KEY)) {
      const token = await refreshSession();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
        res = await nativeFetch(input, { ...init, headers });
      }
    }
    return res;
  };
}

function refreshIfExpiringSoon() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && localStorage.getItem(REFRESH_KEY) && tokenExpiryMs(token) - Date.now() < 2 * 60 * 1000) {
    return refreshSession();
  }
  return Promise.resolve(token);
}

export function installAuthSession() {
  installAxios();
  installFetch();
  setInterval(refreshIfExpiringSoon, 60 * 1000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshIfExpiringSoon(); });
}

export { refreshIfExpiringSoon };
